/**
 * Client-side mirror of Shared.Common CurrencyFormatter ("n" format + prefix/suffix + total digits)
 * for labels the script rewrites. The values come from the server (data-price-format), so the
 * separators, digits and symbol fallback match the server-rendered prices.
 */
export interface PriceFormat {
    prefix: string;
    suffix: string;
    decimalSeparator: string;
    groupSeparator: string;
    decimalDigits: number;
    totalDigits: number;
}

export function parsePriceFormat(json: string | undefined): PriceFormat | null {
    if (!json) {
        return null;
    }
    try {
        const format = JSON.parse(json) as PriceFormat;
        return typeof format?.decimalDigits === 'number' ? format : null;
    } catch {
        return null;
    }
}

export function formatPrice(value: number, format: PriceFormat): string {
    const digits = Math.max(0, Math.min(20, format.decimalDigits));
    const negative = value < 0;
    const [integerPart, fractionPart] = Math.abs(value).toFixed(digits).split('.');

    const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, format.groupSeparator ?? '');
    let result = fractionPart ? `${grouped}${format.decimalSeparator ?? '.'}${fractionPart}` : grouped;
    if (negative) {
        result = `-${result}`;
    }

    // Same as CurrencyFormatter.AddTotalDigits: left-pad with zeros up to the configured integer digits.
    const integerDigits = Math.trunc(Math.abs(value)).toString().length;
    if (integerDigits < format.totalDigits) {
        result = '0'.repeat(format.totalDigits - integerDigits) + result;
    }

    return `${format.prefix ?? ''}${result}${format.suffix ?? ''}`;
}
